import type { useStoredTheme } from "@/utils/theme-context";

type Theme = ReturnType<typeof useStoredTheme>;
const channels = (color: string): number[] | null => {
  const hex = color.match(/^#([\da-f]{6}|[\da-f]{3})$/i)?.[1];
  if (hex) {
    const full = hex.length === 3 ? [...hex].map(value => value + value).join("") : hex;
    return full.match(/../g)!.map(value => parseInt(value, 16) / 255);
  }
  const rgb = color.match(/^rgb\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*\)$/i);
  return rgb ? rgb.slice(1).map(value => Number(value) / 255) : null;
};
const luminance = (rgb: number[]) => rgb.reduce((sum, value, index) => sum +
  (value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4) * [0.2126, 0.7152, 0.0722][index], 0);
const contrast = (first: number[], second: number[]) => {
  const a = luminance(first), b = luminance(second);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
};

// Composer-only: retain configured surfaces and readable labels. Fall back to
// existing semantic tokens rather than changing backend themes or shared Button.
export function composerControlColors(theme: Theme, mode: "light" | "dark") {
  const primary = theme.button.primary;
  const select = (surface: string, preferredColor: string, preferredValue: string) => {
    const background = channels(surface);
    if (!background) return preferredValue;
    const candidates = [
      [preferredValue, preferredColor],
      ["var(--foreground)", theme.general.textColor[mode]],
      ["var(--background)", theme.general.backgroundColor[mode]],
    ].map(([value, color]) => {
      const rgb = channels(color);
      return { value, ratio: rgb ? contrast(rgb, background) : 0 };
    });
    const readable = candidates.find(candidate => candidate.ratio >= 3);
    return (readable ?? candidates.reduce((a, b) => a.ratio >= b.ratio ? a : b)).value;
  };
  return {
    normal: select(primary.backgroundColor[mode], primary.textColor[mode], "var(--primary-foreground)"),
    hover: select(primary.hoverBackgroundColor[mode], primary.hoverTextColor[mode], primary.hoverTextColor[mode]),
  };
}
